import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, Col, DatePicker, Row, Switch, Typography } from 'antd'
import moment from 'moment'

import CustomLabel from '../../../../components/CustomLabel/CustomLabel'
import CustomSelect from '../../../../components/CustomSelect/customSelect'
import { getMessage } from '../../../../util/IntlMessages'
import { api, request } from '../../../../util/handlerRequestUtil'
import { isAdmin, isRtv } from '../../../../util/profiles'
import { DATE_FORMAT, REPORT_TYPE, STATUS, STATUS_IMPORTACAO, TIPO_IMPORTACAO } from '../../Enum'
import ProductivityReportFilter from '../../ProductivityReportFilter'

import './styles.less'

const { RangePicker } = DatePicker

export const Filters = ({
  values,
  errors = {},
  setFieldValueWithState,
  handleClickFilter,
  handleClickClearFilter,
  setFieldValueWithFields,
  loggedUser
}) => {
  const [state, setState] = useState({ advancedFilter: false })
  const [formattedActionTypes, setFormattedActionTypes] = useState([])
  const [formattedOrganizations, setFormattedOrganizations] = useState([])
  const [formattedRegions, setFormattedRegions] = useState([])
  const [formattedDistricts, setFormattedDistricts] = useState([])
  const [formattedMembers, setFormattedMembers] = useState([])
  const [formattedCustomers, setFormattedCustomers] = useState([])
  const [dataPickerSelected, setDataPickerSelected] = useState(false)
  const [timeOptions, setTimeOptions] = useState([])

  const currentUser = useSelector((state) => state.auth.auth)

  const isCurrentUserAdmin = isAdmin(currentUser)
  const isCurrentUserRtv = isRtv(loggedUser)

  const getHarvests = async () => {
    try {
      let harvests = await request(api.listSafra({ active: true }))
      const formattedValues = harvests
        .filter(({ referencePeriodRanges }) => referencePeriodRanges && referencePeriodRanges.length)
        .map((item) => ({
          value: item.id,
          description: item.name,
          initialDate: moment(item.referencePeriodRanges[0].beginDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
          endDate: moment(item.referencePeriodRanges[0].endDate, 'YYYY-MM-DD').format('DD-MM-YYYY')
        }))

      setTimeOptions([...formattedValues, { value: -1, description: ' TODOS' }])
    } catch (error) {
      console.error({ error })
    }
  }

  const getActionTypes = async () => {
    try {
      const actionTypes = await request(api.getActionTypesDropdown({ active: true, todos: false }))
      setFormattedActionTypes(actionTypes.filter((item) => item.value !== '-1'))
    } catch (error) {
      console.error({ error })
    }
  }

  const getDistricts = async (regions) => {
    try {
      if (regions && regions.length === 0) {
        setFormattedDistricts([])
        return []
      }

      const districts = await request(api.listDistrictDropDown({ active: true, regionId: regions, todos: false }))

      if (districts.length === 1) {
        const districtId = districts[0].value
        setFieldValueWithFields({ reportFilterDistrict: [districtId] })
      }

      setFormattedDistricts(districts)

      return districts
    } catch (error) {
      console.error({ error })
    }
  }

  const getRegions = async (organizationId) => {
    try {
      if (organizationId === undefined) return setFormattedRegions([])

      const params = { active: true }
      if (organizationId > 0) {
        params.organizationId = organizationId
      }
      const regions = await request(api.getRegionsDropdown(params))
      setFormattedRegions(regions)

      if (regions.length === 1) {
        const regionId = regions[0].value
        setFieldValueWithFields({ reportFilterRegional: [regionId] })
        await getDistricts([regionId])
      }
    } catch (error) {
      console.error({ error })
    }
  }

  const getOrganizations = async () => {
    try {
      const organizations = await request(api.listOrganizationsDropDown({ active: true, todos: true }))

      if (organizations.length === 1) {
        const organizationId = organizations[0].value
        setFieldValueWithFields({ organization: organizationId })
        await getRegions(organizationId)
      }

      setFormattedOrganizations(organizations)
    } catch (error) {
      console.error({ error })
    }
  }

  const getMembers = async (regionsIds, districtsIds, organization) => {
    try {
      const members = await request(api.listMembers({ active: true, regionsIds, districtsIds, organization }))
      const formattedMembers = members.map((member) => ({
        value: member.id,
        description: member.name,
        regionalIds: member.regions.map(({ id }) => id),
        districtIds: member.districts.map(({ id }) => id)
      }))
      setFormattedMembers(formattedMembers)
    } catch (error) {
      console.error({ error })
    }
  }

  const getCustomers = async ({ regions, districts }) => {
    try {
      const customers = await request(api.getCustomersDropdown({ regionId: regions, districtId: districts }))
      setFormattedCustomers(customers)
    } catch (error) {
      console.error({ error })
    }
  }

  useEffect(() => {
    getHarvests()
    getActionTypes()
    getOrganizations()
  }, [])

  useEffect(() => {
    const { reportFilterRegional, reportFilterDistrict, organization, reportFilterType } = values

    if (['VISITAS_DETALHADA', 'VISITAS'].includes(reportFilterType)) {
      getMembers(reportFilterRegional, reportFilterDistrict, organization)
    }

    if (
      [
        'ACTION_PLAN',
        'CADASTRO_ELEITOS',
        'ELEITOS_E_ALTERNATIVOS',
        'ARRASTAO',
        'FORECAST',
        'VISITAS_DETALHADA',
        'VISITAS'
      ].includes(reportFilterType)
    ) {
      getCustomers({ regions: reportFilterRegional, districts: reportFilterDistrict })
    }
  }, [values.reportFilterRegional, values.reportFilterDistrict, values.organization, values.reportFilterType])

  async function onChangeOrganization(_, value) {
    setFieldValueWithFields({
      organization: value,
      reportFilterRegional: [],
      reportFilterDistrict: [],
      reportFilterCustomer: [],
      reportFilterMember: []
    })

    await getRegions(value)
  }

  const autoRemoveSelections = (params = {}) => {
    const { reportFilterDistrict = [], reportFilterMember = [], reportFilterCustomer = [] } = values
    const { selectedDistricts = reportFilterDistrict, districts = formattedDistricts } = params

    const districtSet = new Set(districts.map((item) => item.value))
    const updatedDistricts = selectedDistricts.filter((districtId) => districtSet.has(districtId))
    const updatedDistrictSet = new Set(updatedDistricts)

    const filterEntitiesByDistricts = (entities, formattedData) => {
      return entities.filter((entityId) => {
        const entity = formattedData.find((item) => item.value === entityId)
        return entity && entity.districtIds.some((districtId) => updatedDistrictSet.has(districtId))
      })
    }

    const updatedCustomers = filterEntitiesByDistricts(reportFilterCustomer, formattedCustomers)
    const updatedMembers = filterEntitiesByDistricts(reportFilterMember, formattedMembers)

    setFieldValueWithFields({
      reportFilterCustomer: updatedCustomers,
      reportFilterMember: updatedMembers,
      reportFilterDistrict: updatedDistricts
    })
  }

  async function onChangeRegion(selectedRegionals = []) {
    const districts = await getDistricts(selectedRegionals)
    setFieldValueWithFields({ reportFilterRegional: selectedRegionals })
    autoRemoveSelections({ districts })
  }

  async function onChangeDistrict(selectedDistricts = []) {
    setFieldValueWithFields({ reportFilterDistrict: selectedDistricts })
    autoRemoveSelections({ selectedDistricts })
  }

  function onChangePeriod(values, interval) {
    const periods = []

    if (values.includes(1)) {
      return (
        interval &&
        setFieldValueWithFields({
          periods: [
            {
              initialDate: interval[0],
              endDate: interval[1]
            }
          ]
        })
      )
    }

    timeOptions.map(({ value, initialDate, endDate }) => {
      if (values.includes(value)) {
        periods.push({ initialDate, endDate, id: value })
      }
    })

    if (values.length) {
      setTimeOptions(timeOptions.filter((obj) => obj.description !== 1))
    }

    setFieldValueWithFields({ periods })
  }

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      advancedFilter: false
    }))
  }, [values.reportFilterType])

  function basicFilters() {
    if (values.reportFilterType === 'CADASTRO_ELEITOS' || values.reportFilterType === 'ELEITOS_E_ALTERNATIVOS') {
      return baseFilter(false, false, true)
    }

    if (
      values.reportFilterType === 'VISITAS' ||
      values.reportFilterType === 'ACTION_PLAN' ||
      values.reportFilterType === 'CARTEIRA_DE_ELEITOS'
    ) {
      return baseFilter(true, false, false)
    }

    if (values.reportFilterType === 'VISITAS_DETALHADA') {
      return actionDetailFilters()
    }

    if (values.reportFilterType === 'FORECAST') {
      return baseFilter(true, false, false)
    }

    if (values.reportFilterType === 'ARRASTAO') {
      return baseFilter(false, false, false)
    }

    if (values.reportFilterType === 'PRODUCTIVITY') {
      return (
        <ProductivityReportFilter
          currentUser={currentUser}
          setFieldValue={setFieldValueWithState}
          values={values}
          formattedRegions={formattedRegions}
          formattedDistricts={formattedDistricts}
          formattedMembers={formattedMembers}
          onChangeOrganization={onChangeOrganization}
          onChangeRegion={onChangeRegion}
          onChangeDistrict={onChangeDistrict}
          customerList={formattedCustomers}
        />
      )
    }

    if (values.reportFilterType === 'CARGA_DE_DADOS') {
      return cargaDadosBasicFilters()
    }
  }

  function actionDetailFilters() {
    const rowSpan = isCurrentUserAdmin ? 4 : 6

    return (
      <>
        <CustomSelect
          showSearch
          field="reportFilterActionType"
          label={getMessage('report_actionType')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={6}
          value={values}
          values={formattedActionTypes}
          required
        />
        {renderPeriodFilter({ span: rowSpan, required: true })}
        <CustomSelect
          showSearch
          field="organization"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          values={formattedOrganizations}
          value={values}
          onChangeWithField={onChangeOrganization}
          rowSpan={rowSpan}
          required
        />
        <CustomSelect
          showSearch
          enable={isCurrentUserAdmin && !values.organization}
          multiple
          field="reportFilterRegional"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          values={formattedRegions}
          onChange={onChangeRegion}
          required={!isCurrentUserAdmin}
        />
        <CustomSelect
          showSearch
          enable={Boolean(!values.reportFilterRegional || values.reportFilterRegional.length === 0)}
          multiple
          field="reportFilterDistrict"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          values={formattedDistricts}
          onChange={onChangeDistrict}
          required={isCurrentUserRtv}
        />
        <CustomSelect
          showSearch
          multiple
          field="reportFilterMember"
          label={getMessage('report_professional')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={6}
          value={values}
          values={formattedMembers}
        />
        <CustomSelect
          showSearch
          multiple
          field="reportFilterCustomer"
          label={getMessage('listCustomer')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={6}
          value={values}
          values={formattedCustomers}
        />
        <CustomSelect
          showSearch
          field="reportFilterStatus"
          label={getMessage('report_status')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={6}
          value={values}
          values={STATUS}
        />
      </>
    )
  }

  function renderPeriodFilter({ span = 6, required = false }) {
    return dataPickerSelected ? (
      <Col span={span}>
        <div style={{ display: 'flex' }}>
          {CustomLabel(getMessage('timeCourse'), { required })}
          <RangePicker
            separator={'-'}
            placeholder={['Data Inicial', 'Data Final']}
            style={{
              marginTop: 8,
              border: '0px solid none',
              backgroundColor: 'transparent'
            }}
            format={DATE_FORMAT}
            onChange={(e, values) => {
              onChangePeriod([1], values)
            }}
          />
          <Button
            icon="close"
            type="ghost"
            style={{
              marginTop: 8,
              width: 30,
              marginLeft: 5
            }}
            onClick={() => setDataPickerSelected(false)}
          />
        </div>
      </Col>
    ) : (
      <CustomSelect
        showSearch
        field="periods"
        label={getMessage('timeCourse')}
        errors={errors}
        setFieldValue={setFieldValueWithState}
        rowSpan={4}
        value={values}
        values={timeOptions}
        multiple={true}
        required
      />
    )
  }

  function cargaDadosBasicFilters() {
    const rowSpan = 5
    return (
      <>
        <CustomSelect
          field="importType"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          values={TIPO_IMPORTACAO}
        />
        <div>
          <Typography>{getMessage('timeCourse')}</Typography>
          <RangePicker
            separator={'-'}
            placeholder={['Data Inicial', 'Data Final']}
            style={{
              marginTop: 8,
              border: '0px solid none',
              backgroundColor: 'transparent'
            }}
            format={DATE_FORMAT}
            onChange={(e, values) => {
              onChangePeriod([1], values)
            }}
          />
        </div>
        <CustomSelect
          field="reportFilterStatus"
          label={getMessage('report_status')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          values={STATUS_IMPORTACAO}
        />
      </>
    )
  }

  function baseFilter(withPeriod, withMembers, withCustomers) {
    const rowSpan = isCurrentUserAdmin ? 4 : 6
    return (
      <>
        {withPeriod && renderPeriodFilter({ span: rowSpan, required: true })}
        <CustomSelect
          showSearch
          field="organization"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          values={formattedOrganizations}
          value={values}
          onChangeWithField={onChangeOrganization}
          rowSpan={rowSpan}
          required
        />
        <CustomSelect
          showSearch
          enable={isCurrentUserAdmin && !values.organization}
          multiple
          field="reportFilterRegional"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          values={formattedRegions}
          onChange={onChangeRegion}
          required={!isCurrentUserAdmin}
        />
        <CustomSelect
          showSearch
          enable={Boolean(!values.reportFilterRegional || values.reportFilterRegional.length === 0)}
          multiple
          field="reportFilterDistrict"
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={rowSpan}
          value={values}
          onChange={onChangeDistrict}
          values={formattedDistricts}
          required={isCurrentUserRtv}
        />
        {withMembers && (
          <CustomSelect
            showSearch
            multiple
            field="reportFilterMember"
            label={getMessage('report_professional')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={rowSpan}
            value={values}
            values={formattedMembers}
          />
        )}
        {withCustomers && (
          <CustomSelect
            showSearch
            multiple
            field="reportFilterCustomer"
            label={getMessage('listCustomer')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={rowSpan}
            value={values}
            values={formattedCustomers}
          />
        )}
      </>
    )
  }

  const showAdvancedFiltersSwitch = useMemo(
    () =>
      ![
        'CADASTRO_ELEITOS',
        'CARTEIRA_DE_ELEITOS',
        'ELEITOS_E_ALTERNATIVOS',
        'VISITAS_DETALHADA',
        'PRODUCTIVITY',
        'CARGA_DE_DADOS',
        undefined
      ].includes(values.reportFilterType),
    [values.reportFilterType]
  )

  function advancedFilters() {
    const isAction = values.reportFilterType === 'VISITAS'
    if (isAction) {
      return (
        <Row type="flex" justify="start" className="report-filters__advanced-filters">
          <CustomSelect
            showSearch
            multiple
            field="reportFilterMember"
            label={getMessage('report_professional')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={4}
            value={values}
            values={formattedMembers}
          />
          <CustomSelect
            showSearch
            multiple
            field="reportFilterCustomer"
            label={getMessage('listCustomer')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={4}
            value={values}
            values={formattedCustomers}
          />
          <CustomSelect
            showSearch
            multiple
            field="reportFilterActionType"
            label={getMessage('report_actionType')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={4}
            value={values}
            values={formattedActionTypes}
          />
          <CustomSelect
            showSearch
            field="reportFilterStatus"
            label={getMessage('report_status')}
            errors={errors}
            setFieldValue={setFieldValueWithState}
            rowSpan={4}
            value={values}
            values={STATUS}
          />
        </Row>
      )
    }

    return (
      <CustomSelect
        showSearch
        multiple
        field="reportFilterCustomer"
        label={getMessage('listCustomer')}
        errors={errors}
        setFieldValue={setFieldValueWithState}
        rowSpan={6}
        value={values}
        values={formattedCustomers}
      />
    )
  }

  return (
    <div className="report-filters">
      <Row className="report-filters__filters-wrapper" type="flex" justify="start">
        <CustomSelect
          field="reportFilterType"
          label={getMessage('report_reportType')}
          errors={errors}
          setFieldValue={setFieldValueWithState}
          rowSpan={6}
          value={values}
          values={REPORT_TYPE}
        />
        {basicFilters()}
      </Row>
      {state.advancedFilter && advancedFilters()}
      <hr className="report-filters__hr" />
      <Row type="flex" justify="space-between" className="report-filters__buttons-wrapper">
        <Col className="report-filters__advanced-switch">
          {showAdvancedFiltersSwitch && (
            <>
              <Typography>{getMessage('advancedFilter')}</Typography>
              <Switch
                checked={state.advancedFilter}
                onChange={() => {
                  if (state.advancedFilter) {
                    setFieldValueWithFields({
                      menuNameCustomerTypes: [],
                      reportFilterActionType: [],
                      classificationElectedLocal: [],
                      advancedFiltersActionPlan: false
                    })
                  }

                  setState({
                    ...state,
                    advancedFilter: !state.advancedFilter
                  })
                }}
              />
            </>
          )}
        </Col>
        <Col className="report-filters__action-buttons">
          <Button
            icon="eye"
            className="bgPrimary report-filters__btn-preview"
            type="dashed"
            onClick={handleClickFilter}
          >
            {getMessage('generatePreview')}
          </Button>
          <Button icon="close" className="report-filters__btn-clear" type="ghost" onClick={handleClickClearFilter}>
            {getMessage('cleanFilters')}
          </Button>
        </Col>
      </Row>
    </div>
  )
}
